import React, { useState } from 'react';
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik';
import DateTimePicker from 'react-widgets/lib/DateTimePicker';
import style from 'react/pages/written_materials/new/style.module.scss';
import { PrimaryButton } from '@magoosh/lib/button';
import { paths } from 'config/path_helpers';
import * as moment from 'moment/moment';
import momentLocalizer from 'react-widgets-moment';
import fetch from 'utilities/fetch';
import { pluralize } from 'utilities/pluralize';
import * as yup from 'yup';
import { Alert } from 'react-bootstrap';

interface Props {
  remainingCredits: number;
  authenticityToken: string;
  writtenMaterialIds: number[];
}
interface WrittenMaterialValues {
  whatWorksBest: string;
  whatNeedsHelp: string;
  prompt: string;
  document: string;
  documentType: string;
  otherDocumentType: string;
  applicationDeadline: Date;
  authenticityToken: string;
}

const validationSchema = yup.object().shape({
  whatWorksBest: yup.string().required('Required').max(10000, 'No more than 10,000 characters'),
  whatNeedsHelp: yup.string().required('Required').max(10000, 'No more than 10,000 characters'),
  prompt: yup.string().required('Required').max(10000, 'No more than 10,000 characters'),
  document: yup.string().required('Required').max(10000, 'No more than 10,000 characters'),
  documentType: yup.string().required('Required'),
  otherDocumentType: yup.string().when('documentType', {
    is: 'Other',
    then: yup.string().required('Required')
  })
});

const documentTypes = ['Statement of Purpose', 'Personal Statement', 'Short Answer', 'Other'];

moment.locale('en');
momentLocalizer(moment);

const AdmissionsEssayForm: React.FC<Props> = (props) => {
  const [error, setError] = useState<string>(null);

  const initialValues: WrittenMaterialValues = {
    whatWorksBest: '',
    whatNeedsHelp: '',
    prompt: '',
    document: '',
    documentType: documentTypes[0],
    otherDocumentType: '',
    authenticityToken: props.authenticityToken,
    applicationDeadline: new Date()
  };

  const onSubmitForm = (
    values: WrittenMaterialValues,
    { setSubmitting, setFieldError }: FormikHelpers<WrittenMaterialValues>
  ) => {
    setSubmitting(true);
    const valuesToSubmit = values;
    if (values.documentType === 'Other') {
      valuesToSubmit.documentType = `Other: ${values.otherDocumentType}`;
    }
    fetch(paths.api.writtenMaterials(), {
      method: 'POST',
      body: JSON.stringify(valuesToSubmit)
    })
      .then((response) => {
        if (response._redirect) {
          window.location.assign(response._redirect);
        }
      })
      .catch((response) => {
        setSubmitting(false);
        if (response.errors._error) {
          setFieldError('general', response.errors._error);
        }
      });
  };

  const renderNoCreditsRemaining = () => {
    return (
      <div>
        <p>
          You don't have any review credits remaining.{' '}
          <a href={paths.subscriptions('foundations-plus-review', 'Foundations_UpgradetoReview22')}>
            Upgrade your Foundations plan
          </a>{' '}
          to include <b>two</b> writing reviews.
        </p>
        <p>
          Already used your writing reviews? Purchase{' '}
          <a href={paths.subscriptions('writing-review-1')}>one review</a> or get{' '}
          <a href={paths.subscriptions('writing-review-3')}>3 reviews</a> for the price of 2.
        </p>
      </div>
    );
  };

  const renderForm = () => {
    return (
      <>
        <Formik initialValues={initialValues} onSubmit={onSubmitForm} validationSchema={validationSchema}>
          {({ isSubmitting, values, submitForm, setFieldValue, errors }) => {
            return (
              <Form>
                <label>
                  What type of document are you submitting?
                  <Field as="select" className="form-control" name="documentType">
                    {documentTypes.map((type) => (
                      <option value={type}>{type}</option>
                    ))}
                  </Field>
                  <ErrorMessage component="div" name="documentType" className={style.error} />
                </label>
                {values.documentType === 'Other' && (
                  <label>
                    Please describe your material.
                    <Field name="otherDocumentType" className="form-control" />
                    <ErrorMessage component="div" name="otherDocumentType" className={style.error} />
                  </label>
                )}
                <label>
                  What do you feel works best in this piece?
                  <Field name="whatWorksBest" component="textarea" />
                  <ErrorMessage component="div" name="whatWorksBest" className={style.error} />
                </label>
                <label>
                  What is the one area in this piece that you would most like help with?
                  <Field name="whatNeedsHelp" component="textarea" />
                  <ErrorMessage component="div" name="whatNeedsHelp" className={style.error} />
                </label>
                <label>
                  What is the prompt you are responding to? Copy/paste here:
                  <Field name="prompt" component="textarea" />
                  <ErrorMessage component="div" name="prompt" className={style.error} />
                </label>
                <label>
                  Paste your document here:
                  <Field name="document" component="textarea" />
                  <ErrorMessage component="div" name="document" className={style.error} />
                </label>
                <label>
                  When is your first application deadline?
                  <DateTimePicker
                    onChange={(value) => setFieldValue('applicationDeadline', value)}
                    value={values.applicationDeadline}
                    format="MM/DD/YYYY"
                    time={false}
                    min={new Date()}
                  />
                </label>
                <PrimaryButton submitting={isSubmitting} onClick={submitForm}>
                  Submit
                </PrimaryButton>
                <p>You have {pluralize(props.remainingCredits, 'credit', 'credits')} remaining.</p>
                <p>You will receive detailed feedback within 7 days.</p>
                {errors['general'] && <Alert bsStyle="danger">{errors['general']}</Alert>}
              </Form>
            );
          }}
        </Formik>
      </>
    );
  };

  if (props.remainingCredits < 1) {
  }

  return (
    <>
      <div className={style.writingMaterialForm}>
        <h1>Submit your material</h1>

        {props.remainingCredits > 1 ? renderForm() : renderNoCreditsRemaining()}
        {props.writtenMaterialIds.length > 0 && (
          <div>
            <h4>Previously Submitted:</h4>
            <ul>
              {props.writtenMaterialIds.map((id, idx) => {
                return (
                  <li>
                    <a href={paths.writtenMaterial(id)}>Submission {idx + 1}</a>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default AdmissionsEssayForm;
